import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Psychology as PsychologyIcon,
  Close as CloseIcon,
  PlayArrow as StartIcon
} from '@mui/icons-material';
import api from '../../services/api';

const REPORT_TYPES = [
  {
    id: '30_day_assessment',
    name: '30-Day Assessment Report',
    description: 'Comprehensive organizational assessment based on IP Catalog methodology',
    icon: AssessmentIcon,
    color: '#2196F3',
    estimatedTime: '10-15 minutes'
  },
  {
    id: 'leadership_development',
    name: 'Leadership Development Plan',
    description: 'Personalized neuroscience-based leadership development roadmap',
    icon: PsychologyIcon,
    color: '#9C27B0',
    estimatedTime: '8-12 minutes'
  },
  {
    id: 'team_dynamics',
    name: 'Team Dynamics Report',
    description: 'Analysis of team effectiveness and improvement recommendations',
    icon: PeopleIcon,
    color: '#FF9800',
    estimatedTime: '6-10 minutes'
  },
  {
    id: 'strategic_growth',
    name: 'Strategic Growth Report',
    description: 'Strategic analysis and growth opportunities assessment',
    icon: TrendingUpIcon,
    color: '#4CAF50',
    estimatedTime: '12-18 minutes'
  }
];

// eslint-disable-next-line max-lines-per-function
const ReportGeneration = ({ open, onClose, entityId, onStartReport }) => {
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [error, setError] = useState(null);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Refs for scrolling and focusing
  const promptSectionRef = useRef(null);
  const templateSectionRef = useRef(null);
  const promptSelectRef = useRef(null);
  const templateSelectRef = useRef(null);
  const submitButtonRef = useRef(null);

  // Load prompts and templates when dialog opens
  useEffect(() => {
    if (open && entityId) {
      loadPromptsAndTemplates();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entityId]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedReportType(null);
      setSelectedPrompt(null);
      setSelectedTemplate(null);
      setError(null);
      setLoadingPrompts(false);
      setLoadingTemplates(false);
    }
  }, [open]);

  // Reset selected prompt when report type changes and scroll to prompt section
  useEffect(() => {
    if (selectedReportType) {
      setSelectedPrompt(null); // Reset prompt selection when report type changes

      // Scroll to prompt section after a short delay
      setTimeout(() => {
        if (promptSectionRef.current) {
          promptSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          // Focus on the prompt select after scrolling
          setTimeout(() => {
            if (promptSelectRef.current) {
              const selectElement = promptSelectRef.current.querySelector('input');
              if (selectElement) {
                selectElement.focus();
              }
            }
          }, 500);
        }
      }, 100);
    }
  }, [selectedReportType]);

  // Scroll to template section when prompt is selected
  useEffect(() => {
    if (selectedPrompt && selectedReportType) {
      // Scroll to template section after a short delay
      setTimeout(() => {
        if (templateSectionRef.current) {
          templateSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          // Focus on the template select after scrolling
          setTimeout(() => {
            if (templateSelectRef.current) {
              const selectElement = templateSelectRef.current.querySelector('input');
              if (selectElement) {
                selectElement.focus();
              }
            }
          }, 500);
        }
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPrompt]);

  // Scroll to submit button when template is selected
  useEffect(() => {
    if (selectedTemplate && selectedPrompt && selectedReportType) {
      // Scroll to submit button after a short delay
      setTimeout(() => {
        if (submitButtonRef.current) {
          submitButtonRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          submitButtonRef.current.focus();
        }
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const loadPromptsAndTemplates = async () => {
    try {
      setLoadingPrompts(true);
      setLoadingTemplates(true);

      // Load available prompts for report generation
      const promptsResponse = await api.getPrompts({ category: 'report_generation' });
      if (promptsResponse.success) {
        setAvailablePrompts(promptsResponse.prompts);
      }
      setLoadingPrompts(false);

      // Load available templates
      const templatesResponse = await api.getDocumentTemplates(entityId);
      if (templatesResponse.success) {
        setAvailableTemplates(templatesResponse.templates);
      }
      setLoadingTemplates(false);
    } catch (error) {
      console.error('Error loading prompts and templates:', error);
      setError('Failed to load configuration options');
      setLoadingPrompts(false);
      setLoadingTemplates(false);
    }
  };

  // Filter prompts based on selected report type
  const getPromptsForReportType = (reportTypeId) => {
    if (!reportTypeId || !availablePrompts) return [];

    return availablePrompts.filter(prompt => {
      // Check if prompt's metadata.reportTypes includes this report type
      return prompt.metadata?.reportTypes?.includes(reportTypeId);
    });
  };

  const handleStartReport = () => {
    if (!selectedReportType || !selectedPrompt || !selectedTemplate) {
      setError('Please select all required options');
      return;
    }

    // Construct the message that will be shown in chat
    const reportConfig = {
      reportType: selectedReportType,
      prompt: selectedPrompt,
      template: selectedTemplate,
      entityId
    };

    // Call parent handler to start report in main chat
    if (onStartReport) {
      onStartReport(reportConfig);
    }

    // Close the modal
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Configure AI Report Generation</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure your report generation settings. The AI will generate the report in the main chat window.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Report Type Selection */}
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            1. Select Report Type
          </Typography>
          <Box sx={{ mb: 3 }}>
            {REPORT_TYPES.map((reportType) => {
              const IconComponent = reportType.icon;
              return (
                <Card
                  key={reportType.id}
                  sx={{
                    mb: 2,
                    cursor: 'pointer',
                    border: selectedReportType?.id === reportType.id ? `2px solid ${reportType.color}` : '1px solid #e0e0e0',
                    '&:hover': { boxShadow: 3 }
                  }}
                  onClick={() => setSelectedReportType(reportType)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <IconComponent sx={{ color: reportType.color, fontSize: 24 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ color: reportType.color, fontWeight: 600 }}>
                          {reportType.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {reportType.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Estimated time: {reportType.estimatedTime}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {/* Prompt Selection */}
          {selectedReportType && (
            <Box ref={promptSectionRef}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                2. Select AI Instruction Set
              </Typography>
              <FormControl fullWidth sx={{ mb: 3 }} ref={promptSelectRef}>
                <InputLabel>Choose AI Instructions</InputLabel>
                <Select
                  value={selectedPrompt?.id || ''}
                  onChange={(e) => {
                    const availablePromptsForType = getPromptsForReportType(selectedReportType.id);
                    const prompt = availablePromptsForType.find(p => p.id === e.target.value);
                    setSelectedPrompt(prompt);
                  }}
                  label="Choose AI Instructions"
                  disabled={loadingPrompts}
                  endAdornment={loadingPrompts && <CircularProgress size={20} sx={{ mr: 2 }} />}
                >
                  {loadingPrompts ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">Loading prompts...</Typography>
                      </Box>
                    </MenuItem>
                  ) : (
                    getPromptsForReportType(selectedReportType.id).map((prompt) => (
                      <MenuItem key={prompt.id} value={prompt.id}>
                        <Box>
                          <Typography variant="body1">{prompt.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {prompt.description} {prompt.version && `(v${prompt.version})`}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {!loadingPrompts && getPromptsForReportType(selectedReportType.id).length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No AI prompts found for this report type. Please contact your administrator to set up prompts for "{selectedReportType.name}".
                </Alert>
              )}
            </Box>
          )}

          {/* Template Selection */}
          {selectedReportType && (
            <Box ref={templateSectionRef}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                3. Select Report Template
              </Typography>
              <FormControl fullWidth sx={{ mb: 3 }} ref={templateSelectRef}>
                <InputLabel>Choose Report Template</InputLabel>
                <Select
                  value={selectedTemplate?.id || ''}
                  onChange={(e) => {
                    const template = availableTemplates.find(t => t.id === e.target.value);
                    setSelectedTemplate(template);
                  }}
                  label="Choose Report Template"
                  disabled={loadingTemplates}
                  endAdornment={loadingTemplates && <CircularProgress size={20} sx={{ mr: 2 }} />}
                >
                  {loadingTemplates ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">Loading templates...</Typography>
                      </Box>
                    </MenuItem>
                  ) : (
                    availableTemplates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        <Box>
                          <Typography variant="body1">{template.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {template.description || 'No description available'}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {!loadingTemplates && availableTemplates.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No templates found. Please create a template for your report.
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          ref={submitButtonRef}
          onClick={handleStartReport}
          variant="contained"
          startIcon={<StartIcon />}
          disabled={!selectedReportType || !selectedPrompt || !selectedTemplate}
        >
          Start Report in Chat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportGeneration;